import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';

import { GooglePlaceAutocompleteComponent } from '@app/shared/google-places/google-place-autocomplete.component';
import { toAddressQueryParam } from '@app/utils/address-query.util';

interface HomeEstimateFaq {
  question: string;
  answer: string;
}

interface HomeEstimateResource {
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

interface HomeEstimateTip {
  title: string;
  description: string;
}

@Component({
  standalone: true,
  imports: [MatButtonModule, GooglePlaceAutocompleteComponent, RouterLink],
  selector: 'app-home-estimate-page',
  templateUrl: './home-estimate-page.component.html',
  styleUrls: ['./home-estimate-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeEstimatePageComponent {
  readonly highlights = [
    'Based on recent sales near your address',
    'Free snapshot — no obligation to list',
    'Local agent follow-up when you are ready',
  ];

  readonly howItWorks = [
    {
      title: 'Enter your address',
      detail: 'Start with the property you want to value — include unit numbers for condos or duplexes when applicable.',
    },
    {
      title: 'Review the estimate',
      detail: 'We pull data from trusted real estate sources to produce a market snapshot for homes like yours nearby.',
    },
    {
      title: 'Talk with an agent',
      detail: 'Automated values are a starting point. A Gabali agent can refine pricing using condition, upgrades, and local comps.',
    },
  ];

  readonly faqs: HomeEstimateFaq[] = [
    {
      question: 'Will I make a profit if I sell now?',
      answer:
        'That depends on your mortgage balance, taxes, and other liens on the property. A Gabali agent can walk through net proceeds before you list.',
    },
    {
      question: 'How much will it cost me to sell?',
      answer:
        'Closing costs, commissions, and prep expenses vary by situation. We will outline what applies to your property and timeline.',
    },
    {
      question: 'What improvements add value before listing?',
      answer:
        'Not every upgrade pays for itself. Your agent can recommend high-impact fixes and help you avoid spending on changes buyers will not reward.',
    },
  ];

  readonly resources: HomeEstimateResource[] = [
    {
      title: "Seller's Guide",
      description: 'See the full selling process — from prep and pricing through marketing and closing.',
      link: '/seller-guide',
      linkLabel: 'Read the guide',
    },
    {
      title: 'Meet Our Agents',
      description: 'Connect with a listing specialist who knows your neighborhood and recent sales data.',
      link: '/agents',
      linkLabel: 'View agents',
    },
    {
      title: 'Browse Active Listings',
      description: 'Compare how similar homes are priced and presented to buyers in Laredo today.',
      link: '/featured-properties',
      linkLabel: 'Browse listings',
    },
    {
      title: 'Request a Consultation',
      description: 'Not ready to list yet? Tell us about your property and we will follow up with a plan.',
      link: '/contact-page',
      linkLabel: 'Contact us',
    },
  ];

  readonly localTips: HomeEstimateTip[] = [
    {
      title: 'Estimates vs. list price',
      description:
        'Online valuations miss condition, lot quirks, and block-level demand. Sold comps in your subdivision are the best benchmark.',
    },
    {
      title: 'South Texas seasonality',
      description:
        'Spring and early summer often bring more buyer traffic, but well-priced homes move year-round when marketed correctly.',
    },
    {
      title: 'Investor interest',
      description:
        'Laredo attracts investor and relocation buyers. Clear disclosures and flexible timing can widen your offer pool.',
    },
    {
      title: 'Compare net proceeds',
      description:
        'The highest offer is not always the best one. Closing credits, repairs, and possession dates affect what you actually walk away with.',
    },
  ];

  readonly placeOptions = {
    componentRestrictions: {
      country: 'US',
    },
  };

  formattedAddress = '';

  @ViewChild('placesRef') placesRef!: GooglePlaceAutocompleteComponent;

  constructor(private readonly router: Router) {}

  openZestimate(): void {
    const address = this.getSelectedAddress();
    if (!address) {
      return;
    }

    this.router.navigate(['/zestimate'], {
      queryParams: { address: toAddressQueryParam(address) },
    });
  }

  handleAddressChange(address: { formatted_address?: string }): void {
    this.formattedAddress = address.formatted_address ?? '';
  }

  private getSelectedAddress(): string {
    const typedAddress = this.placesRef?.getValue()?.trim();
    return this.formattedAddress.trim() || typedAddress || '';
  }
}
